import React, { useMemo } from 'react'
import {
  BarChart as RechartsBarChart,
  Bar,
  CartesianGrid,
  Cell,
  Rectangle,
  Text as RechartsText,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import styled, { css } from 'styled-components'
import { neutralScale } from '../../theme'
import { ChartTooltipBody } from '../chart-tooltip'
import { semanticColors } from '../../theme/tokens'
import {
  BarChartAxis,
  BarChartSeries,
  LemonadeBarChartLegendPosition,
  LemonadeBarChartProps,
} from './bar-chart.types'

const ChartContainer = styled.div`
  & .recharts-wrapper,
  & .recharts-surface,
  & .recharts-bar,
  & path,
  & g {
    outline: none;
  }
`

const BASE_COLOR = 'hsl(263, 100%, 65%)'
const BASE_HUE = 263
const BASE_SAT = 100
const BASE_LIGHT = 65
const MAX_LIGHT = 88

function generateShades(count: number): string[] {
  if (count <= 1) return [BASE_COLOR]

  return Array.from({ length: count }, (_, i) => {
    const lightness = BASE_LIGHT + ((MAX_LIGHT - BASE_LIGHT) * i) / (count - 1)

    return `hsl(${BASE_HUE}, ${BASE_SAT}%, ${Math.round(lightness)}%)`
  })
}

const TICK_STYLE = { fontSize: 12, fill: semanticColors.contentTertiary }

const NUMERIC_TICK_STYLE = {
  fontSize: 12,
  fill: semanticColors.contentDisabled,
}

// Recharts ties the cartesian grid (and a bar's default scale) to the axis whose
// id is the default `0`, so the primary value axis keeps that id and the secondary
// one takes an explicit id. Bars sharing a *category* axis are laid out side by
// side whichever value axis they measure against.
const PRIMARY_VALUE_AXIS_ID = 0
const SECONDARY_VALUE_AXIS_ID = 'secondary'

const valueAxisIdFor = (axis: BarChartAxis | undefined) =>
  axis === 'right' ? SECONDARY_VALUE_AXIS_ID : PRIMARY_VALUE_AXIS_ID

// A labelled vertical value axis needs room beside its ticks for the rotated caption.
const LABELLED_Y_AXIS_WIDTH_PX = 76

const AXIS_LABEL_STYLE = {
  fontSize: 12,
  fill: semanticColors.contentTertiary,
  textAnchor: 'middle' as const,
}

// A vertical chart's value axis runs up the side, so its caption rotates to read
// along it. A horizontal chart's runs along the bottom (its secondary along the
// top), so the caption stays upright, parked at the far end clear of the ticks.
const valueAxisLabel = (
  value: string | undefined,
  { isHorizontal, isSecondary }: { isHorizontal: boolean; isSecondary: boolean }
) => {
  if (value === undefined) return undefined

  if (isHorizontal) {
    return {
      value,
      position: isSecondary
        ? ('insideTopRight' as const)
        : ('insideBottomRight' as const),
      style: AXIS_LABEL_STYLE,
    }
  }

  return {
    value,
    angle: isSecondary ? 90 : -90,
    position: isSecondary ? ('insideRight' as const) : ('insideLeft' as const),
    style: AXIS_LABEL_STYLE,
  }
}

// Room the category caption needs on top of the ticks it sits with: below them on a
// vertical chart (whose categories run along the bottom), beside them on a
// horizontal one (whose categories run up the side). `DEFAULT_CATEGORY_AXIS_HEIGHT_PX`
// mirrors recharts' own default XAxis height, which the caption is added to.
const DEFAULT_CATEGORY_AXIS_HEIGHT_PX = 30
const CATEGORY_AXIS_LABEL_HEIGHT_PX = 20
const CATEGORY_AXIS_LABEL_WIDTH_PX = 20

// The category axis reads left-to-right along the bottom of a vertical chart, so its
// caption stays upright beneath the ticks; on a horizontal chart the categories run
// up the side, so it rotates to read along them.
const categoryAxisCaption = (
  value: string | undefined,
  { isHorizontal }: { isHorizontal: boolean }
) => {
  if (value === undefined) return undefined

  return isHorizontal
    ? {
        value,
        angle: -90,
        position: 'insideLeft' as const,
        style: AXIS_LABEL_STYLE,
      }
    : { value, position: 'insideBottom' as const, style: AXIS_LABEL_STYLE }
}

const SINGLE_RADIUS: [number, number, number, number] = [4, 4, 4, 4]
// Outer end of a stack is rounded; inner end stays flat against the axis (top
// for vertical, right for horizontal — Figma node 5720:5278 / 5669:1017). The
// radius is applied to the outer-most visible segment's own rectangle, so the
// stack animates natively segment-by-segment. Recharts clamps the radius to the
// rectangle's size, so a top segment too thin for the 4px arc simply renders
// flat rather than distorting the stack.
const VERTICAL_STACK_RADIUS: [number, number, number, number] = [4, 4, 0, 0]
const HORIZONTAL_STACK_RADIUS: [number, number, number, number] = [0, 4, 4, 0]
const FLAT_RADIUS: [number, number, number, number] = [0, 0, 0, 0]

// Minimum rendered length (px) for a non-zero stacked segment. A sliver-thin
// segment (e.g. £4 next to £6.32K) would otherwise collapse to a sub-pixel
// height and vanish; when it's the outer-most segment it's also too thin for the
// 4px corner arc, so Recharts clamps the cap flat and the whole bar reads as
// flat-topped (NEX-792). Matched to the corner radius so a floored cap is always
// tall enough to draw the rounded end. Enforced in the segment `shape` (below),
// not via Recharts `minPointSize` — for stacked bars that prop keys off the
// cumulative stack value, not the segment's own, so it would inflate absent
// series (mounted at zero for NEX-685) into phantom slivers.
//
// Trade-off: flooring inflates small segments, so a stack with floored segments
// overshoots its true total height and no longer encodes magnitude precisely.
// Kept deliberately small (presence over precision); exact values stay in the
// tooltip. Compounds when several segments in one stack are floored.
const MIN_STACK_SEGMENT_PX = 4

type StackRectGeometry = {
  x?: number
  y?: number
  width?: number
  height?: number
}

// Total extra px added by every inner segment that gets floored — i.e. the
// amount this segment must shift outward so it stays flush against the (now
// taller) segments stacked inside it. `scale` is px-per-value for this datum's
// shared linear axis; inner heights are `value * scale`.
function cumulativeInnerGrowth(
  innerValues: number[],
  scale: number,
  min: number
): number {
  if (!(scale > 0)) return 0
  return innerValues.reduce((extra, v) => {
    if (v <= 0) return extra
    const h = v * scale
    return h < min ? extra + (min - h) : extra
  }, 0)
}

// Floor a single stacked segment to at least `min` px along the value axis and
// shift it OUTWARD by the cumulative growth of the segments stacked inside it
// (declared earlier), so segments stay flush and never overlap: rightward for
// horizontal bars (inner edge = left), upward for vertical bars (inner edge =
// bottom, where it meets the segment below). `scale` is derived from this
// segment's own value↔pixel ratio — the axis is linear through the origin, so
// any visible segment yields the same px-per-value. Absent/zero segments and
// non-numeric geometry (Recharts can hand undefined mid-layout) pass through.
export function floorStackSegmentGeometry({
  rect,
  isHorizontal,
  min,
  ownValue,
  innerValues,
}: {
  rect: StackRectGeometry
  isHorizontal: boolean
  min: number
  ownValue: number
  innerValues: number[]
}): StackRectGeometry {
  if (!(ownValue > 0)) return rect

  if (isHorizontal) {
    const { x, width } = rect
    if (typeof x !== 'number' || typeof width !== 'number') return rect
    const scale = width > 0 ? width / ownValue : 0
    const shift = cumulativeInnerGrowth(innerValues, scale, min)
    return { ...rect, x: x + shift, width: Math.max(width, min) }
  }

  const { y, height } = rect
  if (typeof y !== 'number' || typeof height !== 'number') return rect
  const scale = height > 0 ? height / ownValue : 0
  const shift = cumulativeInnerGrowth(innerValues, scale, min)
  const grownHeight = Math.max(height, min)
  const innerBottom = y + height - shift
  return { ...rect, y: innerBottom - grownHeight, height: grownHeight }
}

// Lifts the hover tooltip above sibling cards so it isn't painted behind the
// adjacent chart (NEX-681). Mirrors the theme `select` z-index token level.
const TOOLTIP_Z_INDEX = 100

// Regular ticks route through Recharts <Text verticalAnchor="middle"> (emits
// `tspan dy="0.355em"`). The topmost YAxis tick drifts below its grid line in
// Chrome under that path; only the `dominantBaseline="central"` + explicit
// `dy={0}` combo pulls it back. `dy=".35em"` alone does NOT fix it — verified
// empirically — so keep this branch as-is despite looking inconsistent.
type TickProps = {
  x?: string | number
  y?: string | number
  textAnchor?: 'start' | 'middle' | 'end' | 'inherit'
  payload?: { value: string | number }
  index?: number
  visibleTicksCount?: number
}

const renderCenteredTick =
  (formatter?: (value: number) => string, fixTopmost = false) =>
  ({ x, y, textAnchor, payload, index, visibleTicksCount }: TickProps) => {
    const raw = payload?.value
    const text =
      formatter && typeof raw === 'number' ? formatter(raw) : String(raw ?? '')
    const isTopmost =
      fixTopmost &&
      typeof index === 'number' &&
      typeof visibleTicksCount === 'number' &&
      index === visibleTicksCount - 1
    if (isTopmost) {
      return (
        <text
          x={x}
          y={y}
          dy="0"
          textAnchor={textAnchor}
          dominantBaseline="central"
          fontSize={NUMERIC_TICK_STYLE.fontSize}
          fill={NUMERIC_TICK_STYLE.fill}
        >
          {text}
        </text>
      )
    }
    return (
      <RechartsText
        x={x}
        y={y}
        textAnchor={textAnchor}
        verticalAnchor="middle"
        fontSize={NUMERIC_TICK_STYLE.fontSize}
        fill={NUMERIC_TICK_STYLE.fill}
      >
        {text}
      </RechartsText>
    )
  }

const AUTO_CHART_MIN_WIDTH_PX = 280
const AUTO_LEGEND_MIN_WIDTH_PX = 180

const WRAP_TICK_MAX_CHARS = 14
const WRAP_TICK_LINE_HEIGHT_PX = 16
const WRAP_TICK_MAX_LINES = 2
const WRAP_TICK_AXIS_HEIGHT_PX = 120
const WRAP_TICK_LABEL_GAP_PX = 8

interface WrappedRotatedTickProps {
  x?: number
  y?: number
  payload?: { value?: string | number }
}

const WrappedRotatedTick = ({
  x = 0,
  y = 0,
  payload,
}: WrappedRotatedTickProps) => {
  const fullValue = String(payload?.value ?? '')
  // Shared with the horizontal axis: splits on whitespace AND underscores, chunks
  // an oversized unbroken token, and ellipsis-truncates the overflow — so a long
  // single-token org name (e.g. preproduction_organisation_…) wraps/truncates to a
  // bounded footprint instead of sprawling rotated across the axis (NEX-816).
  const lines = wrapAndTruncateCategoryLabel(
    fullValue,
    WRAP_TICK_MAX_CHARS,
    WRAP_TICK_MAX_LINES
  )

  // Centre the stack of rotated lines on the tick so the column doesn't drift
  // into the next bar's slot. In the pre-rotation frame, the +Y direction
  // becomes +X (right) after the -90° rotation, so spreading tspans around
  // y=0 spreads them horizontally around the tick in screen space.
  const lineCount = lines.length

  return (
    <g transform={`translate(${x},${y + WRAP_TICK_LABEL_GAP_PX})`}>
      <text
        transform="rotate(-90)"
        textAnchor="end"
        fontSize={TICK_STYLE.fontSize}
        fill={TICK_STYLE.fill}
      >
        <title>{fullValue}</title>
        {lines.map((line, i) => (
          <tspan
            key={`${i}-${line}`}
            x={0}
            y={(i - (lineCount - 1) / 2) * WRAP_TICK_LINE_HEIGHT_PX}
            dy="0.32em"
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  )
}

const HORIZONTAL_Y_AXIS_AVG_CHAR_PX = 7.5
const HORIZONTAL_Y_AXIS_PADDING_PX = 8
const HORIZONTAL_Y_AXIS_MAX_LINES = 2
const HORIZONTAL_Y_AXIS_LINE_HEIGHT_PX = 14

// Minimum vertical room per bar row on a horizontal chart. The category axis
// renders one label per bar (interval={0}); a label can wrap to
// HORIZONTAL_Y_AXIS_MAX_LINES lines, so each row must reserve at least that
// height (+ a little breathing room) or forced labels overlap their neighbours
// (NEX-816).
const HORIZONTAL_ROW_MIN_PX =
  HORIZONTAL_Y_AXIS_MAX_LINES * HORIZONTAL_Y_AXIS_LINE_HEIGHT_PX + 4
// Vertical space the bottom value axis reserves on a horizontal chart, added on
// top of the rows when deriving the height so the rows themselves keep their
// full per-row allowance.
const HORIZONTAL_VALUE_AXIS_OVERHEAD_PX = 32

function truncateWithEllipsis(value: string, maxChars: number): string {
  if (value.length <= maxChars) return value
  if (maxChars <= 1) return '…'
  return `${value.slice(0, maxChars - 1)}…`
}

// Break a single token into maxChars-sized chunks. Used as a fallback when a
// token has no natural break point (no whitespace or underscores) and is
// itself wider than the axis column — without this, the whole token would
// collapse onto one truncated line and waste the second line.
function chunkOversizedToken(word: string, maxChars: number): string[] {
  if (word.length <= maxChars) return [word]
  const chunks: string[] = []
  for (let i = 0; i < word.length; i += maxChars) {
    chunks.push(word.slice(i, i + maxChars))
  }
  return chunks
}

function wrapAndTruncateCategoryLabel(
  value: string,
  maxChars: number,
  maxLines: number
): string[] {
  if (value.length <= maxChars) return [value]

  // Treat whitespace AND underscores as word boundaries (test/dev names
  // like `preproduction_organisation_xyz` wrap meaningfully). The rejoin
  // path below uses a single space, so underscores are lost in the
  // rendered text — fine because the chart's tooltip still surfaces the
  // original label verbatim.
  const tokens = value
    .split(/[\s_]+/)
    .filter(Boolean)
    .flatMap((token) => chunkOversizedToken(token, maxChars))
  const lines: string[] = []
  let current = ''

  for (const token of tokens) {
    const candidate = current ? `${current} ${token}` : token
    if (candidate.length > maxChars && current) {
      lines.push(current)
      current = token
    } else {
      current = candidate
    }
  }
  if (current) lines.push(current)

  if (lines.length <= maxLines) {
    // A single oversized token (no whitespace to break on) still needs
    // ellipsis truncation so it doesn't overflow the axis width.
    return lines.map((line) => truncateWithEllipsis(line, maxChars))
  }

  // More lines than we allow — keep the leading lines whole and collapse
  // the remainder into the last allowed line with an ellipsis.
  const kept = lines.slice(0, maxLines - 1)
  const overflow = lines.slice(maxLines - 1).join(' ')
  return [...kept, truncateWithEllipsis(overflow, maxChars)]
}

interface TruncatedCategoryTickProps {
  x?: number
  y?: number
  payload?: { value?: string | number }
  width: number
}

const TruncatedCategoryTick = ({
  x = 0,
  y = 0,
  payload,
  width,
}: TruncatedCategoryTickProps) => {
  const fullValue = String(payload?.value ?? '')
  const available = Math.max(0, width - HORIZONTAL_Y_AXIS_PADDING_PX)
  const maxChars = Math.max(
    1,
    Math.floor(available / HORIZONTAL_Y_AXIS_AVG_CHAR_PX)
  )
  const lines = wrapAndTruncateCategoryLabel(
    fullValue,
    maxChars,
    HORIZONTAL_Y_AXIS_MAX_LINES
  )

  // Centre the line stack vertically on the tick (bar's row centre).
  const lineCount = lines.length

  return (
    <text
      textAnchor="end"
      fontSize={TICK_STYLE.fontSize}
      fill={TICK_STYLE.fill}
    >
      <title>{fullValue}</title>
      {lines.map((line, i) => (
        <tspan
          key={`${i}-${line}`}
          x={x}
          y={y + (i - (lineCount - 1) / 2) * HORIZONTAL_Y_AXIS_LINE_HEIGHT_PX}
          dy="0.32em"
        >
          {line}
        </tspan>
      ))}
    </text>
  )
}

const Row = styled.div<{ $position: LemonadeBarChartLegendPosition }>`
  display: flex;
  align-items: center;
  ${({ $position, theme }) => {
    if ($position === 'side') {
      return css`
        flex-direction: row;
        gap: ${theme.sizes.l};
      `
    }
    if ($position === 'bottom') {
      return css`
        flex-direction: column;
        gap: ${theme.sizes.s};
      `
    }
    return css`
      flex-direction: row;
      flex-wrap: wrap;
      gap: ${theme.sizes.l};
    `
  }}
`

const ChartSlot = styled.div<{ $position: LemonadeBarChartLegendPosition }>`
  ${({ $position }) => {
    if ($position === 'side') {
      return css`
        flex: 1 1 60%;
        min-width: 0;
      `
    }
    if ($position === 'auto') {
      return css`
        flex: 1 1 ${AUTO_CHART_MIN_WIDTH_PX}px;
        min-width: 0;
      `
    }
    return css`
      width: 100%;
    `
  }}
`

const LegendSlot = styled.div<{ $position: LemonadeBarChartLegendPosition }>`
  ${({ $position }) => {
    if ($position === 'side') {
      return css`
        flex: 1 1 40%;
        min-width: 0;
      `
    }
    if ($position === 'auto') {
      return css`
        flex: 1 1 ${AUTO_LEGEND_MIN_WIDTH_PX}px;
        min-width: 0;
      `
    }
    return css`
      width: 100%;
    `
  }}
`

type ValueTickRenderer = ReturnType<typeof renderCenteredTick>

// The value-axis contract shared by both orientations. `secondary*` is only used
// when a series targets the secondary axis; the axis doesn't mount otherwise, so a
// single-axis chart renders exactly as it did before.
type ValueAxisParams = {
  fmt: ((v: number) => string) | undefined
  secondaryFmt: ((v: number) => string) | undefined
  hasSecondaryAxis: boolean
  leftAxisLabel?: string
  rightAxisLabel?: string
  categoryAxisLabel?: string
}

function renderHorizontalAxes(
  params: ValueAxisParams & {
    dataKey: string
    showGrid: boolean
    valueTick: ValueTickRenderer
    secondaryValueTick: ValueTickRenderer
    yAxisProps?: Record<string, unknown>
  }
) {
  const yAxisWidth =
    typeof params.yAxisProps?.width === 'number' ? params.yAxisProps.width : 120

  return (
    <>
      <XAxis
        xAxisId={PRIMARY_VALUE_AXIS_ID}
        type="number"
        tickFormatter={params.fmt}
        tick={params.showGrid ? params.valueTick : NUMERIC_TICK_STYLE}
        label={valueAxisLabel(params.leftAxisLabel, {
          isHorizontal: true,
          isSecondary: false,
        })}
        axisLine={false}
        tickLine={false}
      />
      {params.hasSecondaryAxis && (
        <XAxis
          xAxisId={SECONDARY_VALUE_AXIS_ID}
          type="number"
          orientation="top"
          tickFormatter={params.secondaryFmt}
          tick={
            params.showGrid ? params.secondaryValueTick : NUMERIC_TICK_STYLE
          }
          label={valueAxisLabel(params.rightAxisLabel, {
            isHorizontal: true,
            isSecondary: true,
          })}
          axisLine={false}
          tickLine={false}
        />
      )}
      {/* The ticks keep truncating to the caller's own width — the caption's
          allowance widens the axis column rather than squeezing the labels. */}
      <YAxis
        type="category"
        dataKey={params.dataKey}
        // Show a label for every bar. Without this Recharts thins overlapping
        // category ticks, so a busy chart renders ~half its labels, misaligned
        // with the bars. The chart height is grown per bar-count (see
        // chartHeight) to guarantee the room these forced labels need (NEX-816).
        interval={0}
        tick={<TruncatedCategoryTick width={yAxisWidth} />}
        label={categoryAxisCaption(params.categoryAxisLabel, {
          isHorizontal: true,
        })}
        axisLine={false}
        tickLine={false}
        {...params.yAxisProps}
        width={
          params.categoryAxisLabel
            ? yAxisWidth + CATEGORY_AXIS_LABEL_WIDTH_PX
            : yAxisWidth
        }
      />
    </>
  )
}

// A vertical chart's category axis keeps recharts' default height until something
// needs more of it: wrapped ticks turn -90° and need a tall band, and a caption
// needs a line of its own beneath whichever ticks are drawn.
const categoryAxisHeight = ({
  wrapXAxisLabels,
  categoryAxisLabel,
}: {
  wrapXAxisLabels?: boolean
  categoryAxisLabel?: string
}): number | undefined => {
  const ticksHeight = wrapXAxisLabels
    ? WRAP_TICK_AXIS_HEIGHT_PX
    : DEFAULT_CATEGORY_AXIS_HEIGHT_PX

  if (!categoryAxisLabel) return wrapXAxisLabels ? ticksHeight : undefined

  return ticksHeight + CATEGORY_AXIS_LABEL_HEIGHT_PX
}

function renderVerticalAxes(
  params: ValueAxisParams & {
    dataKey: string
    showGrid: boolean
    wrapXAxisLabels?: boolean
    verticalValueTick: ValueTickRenderer
    secondaryVerticalValueTick: ValueTickRenderer
  }
) {
  return (
    <>
      <XAxis
        dataKey={params.dataKey}
        tick={params.wrapXAxisLabels ? <WrappedRotatedTick /> : TICK_STYLE}
        interval={params.wrapXAxisLabels ? 0 : undefined}
        height={categoryAxisHeight(params)}
        label={categoryAxisCaption(params.categoryAxisLabel, {
          isHorizontal: false,
        })}
        axisLine={false}
        tickLine={false}
      />
      <YAxis
        yAxisId={PRIMARY_VALUE_AXIS_ID}
        tickFormatter={params.fmt}
        tick={params.showGrid ? params.verticalValueTick : NUMERIC_TICK_STYLE}
        label={valueAxisLabel(params.leftAxisLabel, {
          isHorizontal: false,
          isSecondary: false,
        })}
        width={params.leftAxisLabel ? LABELLED_Y_AXIS_WIDTH_PX : undefined}
        axisLine={false}
        tickLine={false}
      />
      {params.hasSecondaryAxis && (
        <YAxis
          yAxisId={SECONDARY_VALUE_AXIS_ID}
          orientation="right"
          tickFormatter={params.secondaryFmt}
          tick={
            params.showGrid
              ? params.secondaryVerticalValueTick
              : NUMERIC_TICK_STYLE
          }
          label={valueAxisLabel(params.rightAxisLabel, {
            isHorizontal: false,
            isSecondary: true,
          })}
          width={params.rightAxisLabel ? LABELLED_Y_AXIS_WIDTH_PX : undefined}
          axisLine={false}
          tickLine={false}
        />
      )}
    </>
  )
}

export const LemonadeBarChart = ({
  data,
  dataKey,
  series,
  orientation = 'vertical',
  grouped = false,
  tickFormatter,
  rightTickFormatter,
  leftAxisLabel,
  rightAxisLabel,
  categoryAxisLabel,
  totalKey,
  h = 200,
  w,
  yAxisProps,
  gradientBars,
  wrapXAxisLabels,
  showGrid = false,
  legend,
  legendPosition = 'bottom',
}: LemonadeBarChartProps) => {
  const isHorizontal = orientation === 'horizontal'

  // A horizontal chart shows one label per bar (category YAxis interval={0}), and
  // labels wrap to two lines — so a fixed height with many bars would force the
  // labels to overlap. Grow the height to give every row room for its label.
  // Math.max keeps small charts at the caller's height, so fixed-height cards
  // (e.g. a top-5 breakdown) are unchanged and only over-packed charts grow
  // (NEX-816).
  const chartHeight = isHorizontal
    ? Math.max(
        h,
        data.length * HORIZONTAL_ROW_MIN_PX + HORIZONTAL_VALUE_AXIS_OVERHEAD_PX
      )
    : h

  // The vertical category axis (x-axis) can't fit long org names side by side:
  // Recharts thins overlapping ticks (dropping most labels) and doesn't truncate
  // the survivors, so a long single-token name sprawls across the axis. When any
  // category label is long, rotate + truncate them and show one per bar — the same
  // treatment the horizontal axis gets. An explicit wrapXAxisLabels still wins;
  // all-short-label charts keep the plain horizontal ticks (NEX-816).
  const hasLongCategoryLabels = useMemo(
    () =>
      !isHorizontal &&
      data.some(
        (datum) => String(datum[dataKey] ?? '').length > WRAP_TICK_MAX_CHARS
      ),
    [isHorizontal, data, dataKey]
  )
  const effectiveWrapXAxisLabels = wrapXAxisLabels ?? hasLongCategoryLabels

  const hasSecondaryAxis = series.some((s) => s.axis === 'right')
  // Stacked segments must share one scale to add up to anything, so series split
  // across two value axes are always grouped — whether or not the caller said so.
  const isStacked = series.length > 1 && !grouped && !hasSecondaryAxis

  // A gradient encodes data *position*, so it only applies to a single series —
  // every series in a multi-series chart already carries its own colour.
  const shades = useMemo(
    () =>
      gradientBars && series.length <= 1
        ? generateShades(data.length)
        : undefined,
    [gradientBars, series.length, data.length]
  )

  const layout = isHorizontal ? 'vertical' : 'horizontal'
  const fmt = useMemo(
    () => (tickFormatter ? (v: number) => tickFormatter(v) : undefined),
    [tickFormatter]
  )
  const secondaryFmt = useMemo(
    () =>
      rightTickFormatter ? (v: number) => rightTickFormatter(v) : undefined,
    [rightTickFormatter]
  )
  const valueTick = useMemo(() => renderCenteredTick(fmt), [fmt])
  const verticalValueTick = useMemo(() => renderCenteredTick(fmt, true), [fmt])
  const secondaryValueTick = useMemo(
    () => renderCenteredTick(secondaryFmt),
    [secondaryFmt]
  )
  const secondaryVerticalValueTick = useMemo(
    () => renderCenteredTick(secondaryFmt, true),
    [secondaryFmt]
  )

  // Stacked bars stack natively via `stackId`, so each segment tweens its own
  // rectangle on data/filter changes — the whole stack animates coherently
  // (unlike `<BarStack>`, whose rounded clipPath is fixed at the destination
  // geometry and so detaches from the tweening bars). Corner rounding is applied
  // per-datum on the leaf `<Rectangle>`: the OUTER-most *visible* segment (the
  // last declared series with a non-zero value) carries the rounded cap, even
  // when the topmost declared series is absent for that datum (NEX-618). The
  // shape also floors every visible segment at MIN_STACK_SEGMENT_PX (growing it
  // outward) so sliver-thin segments stay visible and a floored cap is tall
  // enough for the 4px arc to draw rather than clamping flat (NEX-792).
  const seriesShapes = useMemo(() => {
    if (!isStacked) return undefined

    const outerEndRadius = isHorizontal
      ? HORIZONTAL_STACK_RADIUS
      : VERTICAL_STACK_RADIUS

    return series.map((s, idx) => {
      const laterSeriesNames = series.slice(idx + 1).map((other) => other.name)

      // Recharts hands the rendered rectangle's geometry/fill to the `shape`
      // callback plus the row `payload`. Derive the prop type from `<Rectangle>`
      // itself (rather than re-declaring it) and add the `payload` Recharts
      // injects for stacked bars but doesn't surface on RectangleProps.
      return (
        props: React.ComponentProps<typeof Rectangle> & {
          payload?: Record<string, unknown>
        }
      ) => {
        // Falsy match is deliberate: 0/null/undefined/NaN all mean "no visible
        // segment". This series caps the stack when it is itself present and
        // every series stacked above it (declared later) is absent — only that
        // segment carries the rounded end; the rest stay flat.
        const isOuterMostVisible =
          Boolean(props.payload?.[s.name]) &&
          laterSeriesNames.every((name) => !props.payload?.[name])

        // Floor every visible segment so tiny values stay visible, shifting each
        // outward past the inner segments that grew. `innerValues` are the
        // segments stacked inside this one (declared earlier).
        const ownValue = Number(props.payload?.[s.name]) || 0
        const innerValues = series
          .slice(0, idx)
          .map((inner) => Number(props.payload?.[inner.name]) || 0)
        const geometry = floorStackSegmentGeometry({
          rect: props,
          isHorizontal,
          min: MIN_STACK_SEGMENT_PX,
          ownValue,
          innerValues,
        })

        return (
          <Rectangle
            {...props}
            {...geometry}
            radius={isOuterMostVisible ? outerEndRadius : FLAT_RADIUS}
          />
        )
      }
    })
  }, [series, isStacked, isHorizontal])

  // Which value axis this series measures against: the y-axis for a vertical
  // chart, the x-axis for a horizontal one (the other axis carries the categories).
  const valueAxisProps = (s: BarChartSeries) =>
    isHorizontal
      ? { xAxisId: valueAxisIdFor(s.axis) }
      : { yAxisId: valueAxisIdFor(s.axis) }

  const bars = series.map((s, idx) => (
    <Bar
      key={s.name}
      {...valueAxisProps(s)}
      dataKey={s.name}
      name={s.label ?? s.name}
      radius={isStacked ? undefined : SINGLE_RADIUS}
      shape={seriesShapes ? seriesShapes[idx] : undefined}
      stackId={isStacked ? 'stack' : undefined}
      fill={s.color ?? BASE_COLOR}
      maxBarSize={20}
      isAnimationActive
      animationDuration={800}
      animationEasing="ease-out"
    >
      {idx === 0 &&
        shades &&
        data.map((d, i) => (
          <Cell key={`${String(d[dataKey])}-${i}`} fill={shades[i]} />
        ))}
    </Bar>
  ))

  const valueAxes: ValueAxisParams = {
    fmt,
    secondaryFmt,
    hasSecondaryAxis,
    leftAxisLabel,
    rightAxisLabel,
    categoryAxisLabel,
  }

  const axes = isHorizontal
    ? renderHorizontalAxes({
        ...valueAxes,
        dataKey,
        showGrid,
        valueTick,
        secondaryValueTick,
        yAxisProps,
      })
    : renderVerticalAxes({
        ...valueAxes,
        dataKey,
        showGrid,
        wrapXAxisLabels: effectiveWrapXAxisLabels,
        verticalValueTick,
        secondaryVerticalValueTick,
      })

  const chart = (
    <ChartContainer>
      <ResponsiveContainer width={w ?? '100%'} height={chartHeight}>
        <RechartsBarChart
          data={data}
          layout={layout}
          margin={{
            top: showGrid && !isHorizontal ? 8 : 0,
            right: showGrid && isHorizontal ? 16 : 0,
            bottom: 0,
            left: 0,
          }}
        >
          {showGrid && (
            <CartesianGrid
              horizontal={!isHorizontal}
              vertical={isHorizontal}
              stroke={neutralScale.neutral200}
              strokeDasharray="3 3"
            />
          )}
          {axes}
          <Tooltip
            content={
              <ChartTooltipBody formatter={tickFormatter} totalKey={totalKey} />
            }
            cursor={{ fill: 'rgba(139, 74, 255, 0.05)' }}
            // Let the popup escape the chart's SVG viewBox and paint above
            // sibling cards so long-label tooltips on edge bars aren't clipped
            // or hidden behind the adjacent chart (NEX-681).
            allowEscapeViewBox={{ x: true, y: true }}
            wrapperStyle={{ zIndex: TOOLTIP_Z_INDEX }}
          />
          {bars}
        </RechartsBarChart>
      </ResponsiveContainer>
    </ChartContainer>
  )

  if (!legend) return chart

  return (
    <Row $position={legendPosition}>
      <ChartSlot $position={legendPosition}>{chart}</ChartSlot>
      <LegendSlot $position={legendPosition}>{legend}</LegendSlot>
    </Row>
  )
}
